import type { Dictionary } from "lodash";
import { isEmpty, isEqual, isNil, keyBy, sortBy, uniq } from "lodash";
import { ProcessTargetEnum } from "./process-things";
import type { nil } from "./type-utils";

/** A task of a process model as far as its predecessor links are concerned. */
export interface RelinkableTask {
  id?: string;
  modelId: string;
  targetObject?: string;
  predecessorsIds?: readonly string[];
  atLeastOnePredecessorIds?: readonly string[];
}

/** The tree neighbours of a task that only they link to: it waits for the previous one, and only the next one waits for it. */
export interface NeighbourChain {
  previousModelId: string;
  nextModelId: string;
}

/** One task's predecessors before and after the re-link, by entity id. The lists name tasks by model id. */
export interface PredecessorRewrite {
  taskId: string;
  modelId: string;
  previousPredecessorsIds: string[];
  predecessorsIds: string[];
}

/** Why a task cannot wait for the inserted tasks, see {@link planInsertionRewrites}. */
export enum InsertionRewriteBlockedReason {
  Cycle = "Cycle",
  ListTaskPredecessor = "ListTaskPredecessor",
}

/** A task that waits for the inserted tasks, and for the task before the insertion point unless they do already. */
export interface InsertionRewrite extends PredecessorRewrite {
  blockedReason?: InsertionRewriteBlockedReason;
}

/**
 * The neighbours of the task, if they are its only links: its only predecessor is the task before it in the tree, and
 * the task after it is the only one waiting for it, through `predecessorsIds` alone. Only then is it clear how the
 * predecessors follow it to another place, so for any other task this returns `null`.
 *
 * @param orderedTasks the tasks in tree order, see `getTasksInProcessTree`
 */
export function findNeighbourChain(orderedTasks: RelinkableTask[], movedModelId: string): NeighbourChain | null {
  const index = orderedTasks.findIndex(({ modelId }) => modelId === movedModelId);
  if (index < 1) {
    return null;
  }

  const moved = orderedTasks[index];
  const previous = orderedTasks[index - 1];
  const next = orderedTasks[index + 1];
  if (isNil(next)) {
    return null;
  }

  const waitsOnlyForPrevious =
    isEqual(uniq(moved.predecessorsIds ?? []), [previous.modelId]) && isEmpty(moved.atLeastOnePredecessorIds);
  if (!waitsOnlyForPrevious) {
    return null;
  }

  const waitingTasks = orderedTasks.filter((task) => waitsFor(task, movedModelId));
  // A next task that also lists the moved one as an alternative would still wait for it after the re-link, which only
  // rewrites predecessorsIds
  const isOnlyWaitedForByNext =
    waitingTasks.length === 1 &&
    waitingTasks[0].modelId === next.modelId &&
    (next.predecessorsIds ?? []).includes(movedModelId) &&
    !(next.atLeastOnePredecessorIds ?? []).includes(movedModelId);
  if (!isOnlyWaitedForByNext) {
    return null;
  }

  return { previousModelId: previous.modelId, nextModelId: next.modelId };
}

/**
 * The predecessor changes that carry a moved chain task to its new place:
 * - the old next task waits for the old previous task instead,
 * - the moved task waits for its new previous task (or for none, when it is the first task now),
 * - the new next task waits for the moved task instead of the new previous task. It is only touched if it waited for
 *   that task (or, when the moved task is the first one now, if it waited for none), since anything else is a flow the
 *   move did not land in.
 *
 * Returns the changes in tree order, and leaves out tasks whose predecessors stay the same. Returns nothing when the
 * task kept its place in the flow, and nothing at all when any of the changes would break the model: a list task
 * waiting for a task that is not one, or a cycle. Splicing in only part of the changes would leave the flow torn.
 *
 * @param orderedTasksAfterMove the tasks in tree order once the move is saved, see `getTasksInProcessTree`
 */
export function planPredecessorRewrites(
  chain: NeighbourChain,
  orderedTasksAfterMove: RelinkableTask[],
  movedModelId: string
): PredecessorRewrite[] {
  const index = orderedTasksAfterMove.findIndex(({ modelId }) => modelId === movedModelId);
  if (index < 0) {
    return [];
  }

  const moved = orderedTasksAfterMove[index];
  const newPrevious = orderedTasksAfterMove[index - 1];
  const newNext = orderedTasksAfterMove[index + 1];
  if (newPrevious?.modelId === chain.previousModelId) {
    return [];
  }

  const tasksByModelId = keyBy(orderedTasksAfterMove, ({ modelId }) => modelId);
  const oldNext = tasksByModelId[chain.nextModelId];

  const rewrites: PredecessorRewrite[] = [
    ...(isNil(oldNext) ? [] : [replacePredecessor(oldNext, movedModelId, chain.previousModelId)]),
    {
      taskId: moved.id,
      modelId: moved.modelId,
      previousPredecessorsIds: toList(moved.predecessorsIds),
      predecessorsIds: isNil(newPrevious) ? [] : [newPrevious.modelId],
    },
    ...getNewNextRewrites(newNext, newPrevious, movedModelId),
  ];

  const changes = rewrites.filter(
    ({ previousPredecessorsIds, predecessorsIds }) => !isEqual(previousPredecessorsIds, predecessorsIds)
  );
  if (hasInvalidListTaskPredecessor(changes, tasksByModelId) || closesCycle(changes, tasksByModelId)) {
    return [];
  }

  const treeIndexByModelId = new Map(orderedTasksAfterMove.map(({ modelId }, treeIndex) => [modelId, treeIndex]));
  return sortBy(changes, ({ modelId }) => treeIndexByModelId.get(modelId));
}

/**
 * The tasks that wait for nothing but the task directly before an insertion point and should wait for the inserted
 * tasks as well, now that those sit between the two: the inserted tasks that no other inserted task waits for (the
 * last ones of the inserted flow) become their predecessors. Where those wait for the task before the insertion point
 * already, they replace it; otherwise it stays, since an inserted task without predecessors starts right away and
 * the waiting task must not start before the task it waited for.
 *
 * Every task gets its own rewrite, since each can be kept or dropped on its own. A rewrite that would make a list task
 * wait for a task that is not one, or close a cycle, is still returned, with the reason it must not be applied.
 *
 * @param tasks the tasks of the model with the inserted ones, in tree order
 * @param insertionPointModelId the task directly before the insertion point, or nil when there is none
 * @param insertedModelIds the inserted tasks, in tree order
 */
export function planInsertionRewrites(
  tasks: RelinkableTask[],
  insertionPointModelId: string | nil,
  insertedModelIds: string[]
): InsertionRewrite[] {
  if (isNil(insertionPointModelId) || isEmpty(insertedModelIds)) {
    return [];
  }

  const tasksByModelId = keyBy(tasks, ({ modelId }) => modelId);
  const lastInsertedModelIds = getLastInsertedModelIds(insertedModelIds, tasksByModelId);
  const isInsertionPointAwaited = lastInsertedModelIds.every((modelId) =>
    waitsForTransitively(modelId, insertionPointModelId, tasksByModelId)
  );
  const predecessorsIds = isInsertionPointAwaited
    ? lastInsertedModelIds
    : [insertionPointModelId, ...lastInsertedModelIds];
  const waitingTasks = tasks.filter((task) => {
    const isInserted = insertedModelIds.includes(task.modelId);
    const waitsOnlyForInsertionPoint =
      isEqual(uniq(task.predecessorsIds ?? []), [insertionPointModelId]) && isEmpty(task.atLeastOnePredecessorIds);
    return !isInserted && waitsOnlyForInsertionPoint;
  });

  return waitingTasks.map((task) => {
    const rewrite: PredecessorRewrite = {
      taskId: task.id,
      modelId: task.modelId,
      previousPredecessorsIds: toList(task.predecessorsIds),
      predecessorsIds,
    };
    const blockedReason = getInsertionRewriteBlockedReason(rewrite, tasksByModelId);
    return isNil(blockedReason) ? rewrite : { ...rewrite, blockedReason };
  });
}

function getLastInsertedModelIds(insertedModelIds: string[], tasksByModelId: Dictionary<RelinkableTask>): string[] {
  const waitedForByInserted = new Set(
    insertedModelIds.flatMap((modelId) => {
      const task = tasksByModelId[modelId];
      return [...(task?.predecessorsIds ?? []), ...(task?.atLeastOnePredecessorIds ?? [])];
    })
  );
  const lastInserted = insertedModelIds.filter((modelId) => !waitedForByInserted.has(modelId));
  // Only a cycle among the inserted tasks leaves none of them unwaited for; the last one is the best guess there
  return isEmpty(lastInserted) ? insertedModelIds.slice(-1) : lastInserted;
}

/**
 * Whether a task waits for another one, directly or through the predecessors it waits for. A task of
 * `atLeastOnePredecessorIds` does not count: the task may start after another one of that list.
 */
function waitsForTransitively(
  modelId: string,
  awaitedModelId: string,
  tasksByModelId: Dictionary<RelinkableTask>
): boolean {
  const visited = new Set([modelId]);
  let waitedFor = [modelId];
  // Level by level, every task once: a model's flows join and fork a lot, and may hold a cycle
  while (!isEmpty(waitedFor)) {
    const predecessors = uniq(waitedFor.flatMap((waiting) => tasksByModelId[waiting]?.predecessorsIds ?? []));
    if (predecessors.includes(awaitedModelId)) {
      return true;
    }

    waitedFor = predecessors.filter((predecessor) => !visited.has(predecessor));
    waitedFor.forEach((predecessor) => visited.add(predecessor));
  }

  return false;
}

function getInsertionRewriteBlockedReason(
  rewrite: PredecessorRewrite,
  tasksByModelId: Dictionary<RelinkableTask>
): InsertionRewriteBlockedReason | null {
  if (hasInvalidListTaskPredecessor([rewrite], tasksByModelId)) {
    return InsertionRewriteBlockedReason.ListTaskPredecessor;
  }

  if (closesCycle([rewrite], tasksByModelId)) {
    return InsertionRewriteBlockedReason.Cycle;
  }

  return null;
}

function getNewNextRewrites(
  newNext: RelinkableTask | nil,
  newPrevious: RelinkableTask | nil,
  movedModelId: string
): PredecessorRewrite[] {
  if (isNil(newNext)) {
    return [];
  }

  if (isNil(newPrevious)) {
    const waitsForNone = isEmpty(newNext.predecessorsIds) && isEmpty(newNext.atLeastOnePredecessorIds);
    return waitsForNone
      ? [
          {
            taskId: newNext.id,
            modelId: newNext.modelId,
            previousPredecessorsIds: toList(newNext.predecessorsIds),
            predecessorsIds: [movedModelId],
          },
        ]
      : [];
  }

  return (newNext.predecessorsIds ?? []).includes(newPrevious.modelId)
    ? [replacePredecessor(newNext, newPrevious.modelId, movedModelId)]
    : [];
}

function replacePredecessor(task: RelinkableTask, removedModelId: string, addedModelId: string): PredecessorRewrite {
  const previousPredecessorsIds = toList(task.predecessorsIds);
  return {
    taskId: task.id,
    modelId: task.modelId,
    previousPredecessorsIds,
    predecessorsIds: uniq(
      previousPredecessorsIds.map((modelId) => (modelId === removedModelId ? addedModelId : modelId))
    ),
  };
}

function waitsFor(task: RelinkableTask, modelId: string): boolean {
  return (task.predecessorsIds ?? []).includes(modelId) || (task.atLeastOnePredecessorIds ?? []).includes(modelId);
}

function toList(modelIds: readonly string[] | nil): string[] {
  return isNil(modelIds) ? [] : Array.from(modelIds);
}

/** The same rule the backend validates: a list task may only wait for list tasks. */
function hasInvalidListTaskPredecessor(
  rewrites: PredecessorRewrite[],
  tasksByModelId: Dictionary<RelinkableTask>
): boolean {
  return rewrites.some(({ modelId, predecessorsIds }) => {
    const isListTask = tasksByModelId[modelId]?.targetObject === ProcessTargetEnum.Collection;
    return (
      isListTask &&
      predecessorsIds.some((predecessorId) => {
        const predecessor = tasksByModelId[predecessorId];
        return !isNil(predecessor) && predecessor.targetObject !== ProcessTargetEnum.Collection;
      })
    );
  });
}

/**
 * Whether a rewritten task would end up waiting for itself. Every cycle the re-link could close runs through one of
 * the edges it adds, so through one of the rewritten tasks.
 */
function closesCycle(rewrites: PredecessorRewrite[], tasksByModelId: Dictionary<RelinkableTask>): boolean {
  const rewrittenPredecessorsByModelId = new Map(
    rewrites.map(({ modelId, predecessorsIds }) => [modelId, predecessorsIds])
  );
  const getWaitedFor = (modelId: string): string[] => {
    const task = tasksByModelId[modelId];
    if (isNil(task)) {
      return [];
    }

    return [
      ...(rewrittenPredecessorsByModelId.get(modelId) ?? task.predecessorsIds ?? []),
      ...(task.atLeastOnePredecessorIds ?? []),
    ];
  };

  return rewrites.some(({ modelId }) => waitsTransitivelyFor(getWaitedFor(modelId), modelId, getWaitedFor));
}

function waitsTransitivelyFor(
  startModelIds: string[],
  targetModelId: string,
  getWaitedFor: (modelId: string) => string[]
): boolean {
  const visitedModelIds = new Set<string>();
  const visit = (modelId: string): boolean => {
    if (modelId === targetModelId) {
      return true;
    }

    if (visitedModelIds.has(modelId)) {
      return false;
    }

    visitedModelIds.add(modelId);
    return getWaitedFor(modelId).some(visit);
  };

  return startModelIds.some(visit);
}
